import { useState, useEffect } from "react";
import { UserAuth } from "../../context/AuthContext";
import QrCodeCard from "../QrCodeCard/QrCodeCard";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import ErrorModal from "../ErrorModal/ErrorModal";
import DeleteConfirmationModal from "../DeleteConfirmationModal/DeleteConfirmationModal";
import "./qrCodeCardGrid.css";

interface QrCode {
  _id: string;
  image: string;
  title: string;
  fullUrl: string;
  shortenedUrl: string;
  totalVisits: number;
  createdDate: string;
  description?: string;
}

const QrCodeCardGrid = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [qrCodes, setQrCodes] = useState<QrCode[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [modalShow, setModalShow] = useState(false);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [selectedQrCode, setSelectedQrCode] = useState<QrCode | null>(null);
  const { user } = UserAuth();

  const ITEMS_PER_PAGE = 6;

  const fetchUserQrCodes = async () => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/qrcodes/user/${user.uid}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `HTTP Error: ${response.status} - ${response.statusText}`
        );
      }

      const responseData = await response.json();
      setQrCodes(responseData.data);
      setTotalPages(Math.ceil(responseData.data.length / ITEMS_PER_PAGE));
    } catch (error) {
      console.error("Error fetching QR codes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserQrCodes();
    }
  }, [user]);

  const handleDeleteClick = (qrCode: QrCode) => {
    setSelectedQrCode(qrCode);
    setDeleteModalShow(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedQrCode) {
      await deleteQrCode(selectedQrCode._id, selectedQrCode.image);
      setDeleteModalShow(false);
      setSelectedQrCode(null);
    }
  };

  const deleteQrCode = async (id: string, imageUrl: string) => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/qrcodes/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete QR code");
      }

      await response.json();
      setQrCodes(qrCodes.filter((qr) => qr._id !== id));
      setTotalPages(Math.ceil((qrCodes.length - 1) / ITEMS_PER_PAGE));

      if (
        currentPage > 0 &&
        currentPage >= Math.ceil((qrCodes.length - 1) / ITEMS_PER_PAGE)
      ) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const paginatedQrCodes = qrCodes.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  return (
    <div className="qr-code-grid">
      <ErrorModal show={modalShow} onHide={() => setModalShow(false)} />
      <DeleteConfirmationModal
        show={deleteModalShow}
        onHide={() => setDeleteModalShow(false)}
        onConfirm={handleDeleteConfirm}
        title={selectedQrCode?.title || ""}
      />
      {loading && <LoadingSpinner />}

      {qrCodes.length === 0 ? (
        <div className="qr-code-grid__empty">
          <p>
            You don't have any QR codes yet. Click the "Create QR Code" button
            to get started.
          </p>
        </div>
      ) : (
        <>
          <div className="qr-code-grid__container">
            {paginatedQrCodes.map((qrCode) => (
              <QrCodeCard
                key={qrCode._id}
                qrCode={qrCode}
                onDelete={() => handleDeleteClick(qrCode)}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="qr-code-grid__pagination">
              <button
                className="pagination__button"
                onClick={() => setCurrentPage(0)}
                disabled={currentPage === 0}
              >
                {"<<"}
              </button>
              <button
                className="pagination__button"
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
              >
                {"<"}
              </button>
              <span className="pagination__pageNum">
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                className="pagination__button"
                onClick={() =>
                  setCurrentPage(Math.min(totalPages - 1, currentPage + 1))
                }
                disabled={currentPage === totalPages - 1}
              >
                {">"}
              </button>
              <button
                className="pagination__button"
                onClick={() => setCurrentPage(totalPages - 1)}
                disabled={currentPage === totalPages - 1}
              >
                {">>"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QrCodeCardGrid;
